<template>
  <div ref="wrapperRef" class="page-header-actions-wrapper">
    <!-- Action buttons: inline in top bar on all breakpoints (mobile: compact/icon-only via CSS) -->
    <div class="actions-container gap-1 xl:gap-2 [&_button]:px-2 [&_button.bg-primary]:px-3 [&_button]:sm:px-4 [&_button]:py-2">
      <BaseButton
        v-for="action in visibleActions"
        :key="action.id"
        :variant="action.variant"
        :size="effectiveSize"
        :disabled="action.disabled || action.loading"
        :icon="action.icon"
        :icon-size="iconSize"
        :aria-label="action.label"
        @click="() => action.onClick()"
      >
        <span class="action-label whitespace-nowrap">{{ action.labelMobile ?? action.label }}</span>
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import type { Component } from 'vue'
import type { ButtonVariant } from '@/types/ui'

export interface PageHeaderAction {
  id: string
  label: string
  labelMobile?: string
  variant: ButtonVariant
  icon?: Component | null
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  show?: boolean | (() => boolean)
  order?: number
}

interface Props {
  actions: PageHeaderAction[]
  size?: 'xs' | 'small' | 'medium' | 'large'
  spacing?: 'tight' | 'normal' | 'loose'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  spacing: 'normal'
})

// When rendered inside TopBar (teleported into #topbar-page-actions), use xs for parity with store-driven path
const topBarActionSize = inject<undefined | 'xs' | 'small' | 'medium' | 'large'>('topBarActionSize', undefined)
const effectiveSize = computed(() => topBarActionSize ?? props.size)

// Computed properties
const visibleActions = computed(() => {
  return props.actions
    .filter(action => {
      if (action.show === undefined) return true
      if (typeof action.show === 'boolean') return action.show
      if (typeof action.show === 'function') return action.show()
      return true
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0))
})

const iconSize = computed(() => {
  const sizeMap: Record<string, 'small' | 'medium' | 'large'> = {
    xs: 'small',
    small: 'small',
    medium: 'small',
    large: 'medium'
  }
  return sizeMap[effectiveSize.value] || 'small'
})

// Refs
const wrapperRef = ref<HTMLElement>()
</script>

<style scoped>
.page-header-actions-wrapper {
  display: flex;
  align-items: center;
}

.actions-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.actions-container .action-label {
  white-space: nowrap;
}

/* Mobile: keep actions in top bar; hide label on smallest screens so icon-only fits (matches reference) */
@media (max-width: 639px) {
  .actions-container .action-label {
    display: none;
  }

  .actions-container :deep(button) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    min-width: 2.25rem;
  }
}
</style>

